import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, Plus, PencilSimple } from "@phosphor-icons/react";
import { createCharacter, uploadBaseImage, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { VisualTagField } from "@/components/VisualTagField";

function notifyMainCharactersUpdated() {
  window.dispatchEvent(new CustomEvent("main-characters-updated"));
}

function EmptyMainSlot({ storyId, slotIndex, onCreated }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const c = await createCharacter(storyId, name.trim(), true);
      toast.success(`"${name}" locked as Main Character ${slotIndex + 1}`);
      setName("");
      onCreated(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add main character");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`main-character-slot-${slotIndex}`}
      className="border border-dashed border-slate-700 rounded-sm p-3 flex flex-col items-center gap-2 bg-slate-950/40"
    >
      <div className="w-16 h-16 rounded-full border border-dashed border-slate-700 flex items-center justify-center text-slate-600">
        <User size={24} />
      </div>
      <Input
        data-testid={`main-character-name-input-${slotIndex}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder={`Main Character ${slotIndex + 1} name`}
        className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs text-center"
      />
      <Button
        data-testid={`main-character-create-btn-${slotIndex}`}
        onClick={submit}
        disabled={saving || !name.trim()}
        size="sm"
        className="h-7 bg-fuchsia-600 hover:bg-fuchsia-500 rounded-sm text-xs w-full"
      >
        <Plus size={12} className="mr-1" /> Lock Slot {slotIndex + 1}
      </Button>
    </div>
  );
}

function FilledMainSlot({ character, slotIndex, onUpdated }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const updated = await uploadBaseImage(character.id, file);
      toast.success(`Base reference image set for ${character.name}`);
      onUpdated(updated);
      notifyMainCharactersUpdated();
    } catch (err) {
      toast.error("Failed to upload base reference image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div
      data-testid={`main-character-slot-${slotIndex}`}
      className="border border-fuchsia-500/40 rounded-sm p-3 flex flex-col items-center gap-2 bg-slate-950/40"
    >
      <div className="relative group w-16 h-16">
        {character.base_image_path ? (
          <img
            data-testid={`main-character-thumbnail-${character.id}`}
            src={fileUrl(character.base_image_path)}
            alt={character.name}
            className="w-16 h-16 rounded-full object-cover border-2 border-fuchsia-500"
          />
        ) : (
          <div
            data-testid={`main-character-thumbnail-${character.id}`}
            className="w-16 h-16 rounded-full border-2 border-dashed border-amber-500 flex items-center justify-center text-amber-500"
          >
            <User size={24} />
          </div>
        )}
        <button
          data-testid={`main-character-change-btn-${character.id}`}
          onClick={() => fileInputRef.current.click()}
          title="Replace base reference image"
          disabled={uploading}
          className="absolute inset-0 flex items-center justify-center rounded-full bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity text-fuchsia-400"
        >
          <PencilSimple size={16} />
        </button>
        <input
          data-testid={`main-character-change-input-${character.id}`}
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleChange}
        />
      </div>
      <span className="text-xs font-semibold text-slate-200 text-center truncate w-full">{character.name}</span>
      {!character.base_image_path && (
        <span className="text-[9px] text-amber-500 uppercase tracking-wide">No base image yet</span>
      )}
      <div className="w-full pt-1 border-t border-slate-800/80">
        <VisualTagField character={character} onChanged={onUpdated} />
      </div>
    </div>
  );
}

export default function MainCharactersPanel({ storyId, characters, setCharacters }) {
  const mainCharacters = characters.filter((c) => c.is_main);

  const handleCreated = (c) => {
    setCharacters((prev) => (prev.some((p) => p.id === c.id) ? prev.map((p) => (p.id === c.id ? c : p)) : [...prev, c]));
    notifyMainCharactersUpdated();
  };
  const handleUpdated = (updated) => {
    setCharacters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  return (
    <div data-testid="main-characters-panel" className="space-y-2 border border-fuchsia-500/20 rounded-sm p-3 bg-fuchsia-950/10">
      <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-fuchsia-400">
        Main Characters (max 2) — Base Reference Images
      </h3>
      <p className="text-[11px] text-slate-500">
        The reference image ALONE is not enough — Grok's image-edit model needs the Physical Description
        below written out too (hair, skin tone, body build, outfit) or it will invent its own look. Click a
        locked slot's thumbnail to swap the image anytime.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        {[0, 1].map((i) =>
          mainCharacters[i] ? (
            <FilledMainSlot key={mainCharacters[i].id} character={mainCharacters[i]} slotIndex={i} onUpdated={handleUpdated} />
          ) : (
            <EmptyMainSlot key={`empty-${i}`} storyId={storyId} slotIndex={i} onCreated={handleCreated} />
          )
        )}
      </div>
    </div>
  );
}
